import { useMemo, useRef, useState } from 'react'
import { Button, Group, Select, Tabs, Text, Title } from '@mantine/core'
import { isKnownScreen } from '@racedash/shared'
import type { ScreenEntry } from '@racedash/shared'
import ScreenView from '../dashboard/ScreenView'
import ScreenSwitcher from '../dashboard/ScreenSwitcher'
import ScreenManager from '../dashboard/edit/ScreenManager'
import { sampleCar, sampleContext, sampleData, sampleScenarios } from '../dashboard/sampleData'
import type { SampleScenario } from '../dashboard/sampleData'
import { screensForTemplate } from '../dashboard/templates'
import { useNow } from '../dashboard/useSessionData'

/**
 * Every screen against sample data.
 *
 * Outside the shell and outside auth, because it reads nothing: no Firestore,
 * no organisation, no session. It exists so a layout, the type scale and now the
 * timing and stint screens can be judged without waiting for something to be
 * capturing — which for most of a season is most of the time.
 *
 * Nothing here is saved; there is no dashboard behind it.
 */
export default function DashboardPreview() {
  const stage = useRef<HTMLDivElement | null>(null)
  const now = useNow()
  const [scenario, setScenario] = useState<SampleScenario>('midRace')
  const [screens, setScreens] = useState<ScreenEntry[]>(() => screensForTemplate('standard'))
  const [activeId, setActiveId] = useState<string | null>(null)
  const [editing, setEditing] = useState(false)

  const data = useMemo(() => sampleData(scenario), [scenario])
  // A team as well as a race, or a third of the tiles cannot be previewed.
  const context = useMemo(() => sampleContext(now), [now])
  const active = screens.find((s) => s.id === activeId) ?? screens[0] ?? null

  return (
    <div className="preview-page">
      <Group justify="space-between" align="flex-end" mb="md" wrap="wrap">
        <div>
          <Title order={1} size="h3">
            Screen preview
          </Title>
          <Text c="dimmed" size="sm">
            Every screen against a made-up Fun Cup enduro. Nothing here is live, and nothing you
            change is saved. {sampleScenarios.find((s) => s.id === scenario)?.description}
          </Text>
        </div>
        <Group gap="xs" align="flex-end">
          <Select
            size="sm"
            w={190}
            label="Scenario"
            data={sampleScenarios.map((s) => ({ value: s.id, label: s.label }))}
            value={scenario}
            onChange={(v) => v && setScenario(v as SampleScenario)}
            allowDeselect={false}
          />
          <Button
            variant="default"
            disabled={!editing}
            onClick={() => setScreens(screensForTemplate('standard'))}
          >
            Reset
          </Button>
          <Button variant="default" onClick={() => setEditing((e) => !e)}>
            {editing ? 'Done' : 'Edit screens'}
          </Button>
          <Button variant="default" onClick={() => void stage.current?.requestFullscreen?.()}>
            Full screen
          </Button>
        </Group>
      </Group>

      <Group gap="sm" mb="xs" align="center" wrap="wrap">
        {screens.length > 0 && (
          <Tabs value={active?.id ?? null} onChange={setActiveId} variant="pills">
            <Tabs.List>
              {screens.map((s) => (
                <Tabs.Tab key={s.id} value={s.id}>
                  {s.name}
                  {!isKnownScreen(s) && ' ⚠'}
                </Tabs.Tab>
              ))}
            </Tabs.List>
          </Tabs>
        )}
        {editing && (
          <ScreenManager screens={screens} onChange={setScreens} activeId={active?.id ?? null} />
        )}
      </Group>

      <div className="dash-frame" ref={stage}>
        <ScreenSwitcher
          screens={screens}
          activeId={active?.id ?? null}
          onSelect={setActiveId}
          stageRef={stage}
        />
        {active ? (
          <ScreenView
            screen={active}
            editing={editing}
            onChange={(next) => setScreens((cur) => cur.map((s) => (s.id === next.id ? next : s)))}
            data={data}
            context={context}
          primaryCar={sampleCar}
            now={now}
          />
        ) : (
          <div className="dash-empty">
            <p>No screens. Add one with “Edit screens”.</p>
          </div>
        )}
      </div>
    </div>
  )
}
